import { useState, useEffect } from 'react';
import API from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Search, Plus, Users, Calendar } from 'lucide-react';
import { toast } from 'sonner';

export default function PatientsPage() {
  const [patients, setPatients] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: '', date_of_birth: '', gender: '', phone: '', email: '', medical_history: '' });

  const fetchPatients = async () => {
    try {
      const res = await API.get(`/patients?search=${search}&limit=50`);
      setPatients(res.data.patients || []);
      setTotal(res.data.total || 0);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchPatients(); }, [search]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await API.post('/patients', form);
      toast.success('Patient added successfully');
      setDialogOpen(false);
      setForm({ name: '', date_of_birth: '', gender: '', phone: '', email: '', medical_history: '' });
      fetchPatients();
    } catch (err) { toast.error('Failed to add patient'); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6 max-w-[1400px]">
      <div className="flex items-center justify-between">
        <div>
          <h1 data-testid="patients-title" className="text-3xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>Patients</h1>
          <p className="text-slate-500 mt-1">{total} patients registered</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-patient-btn" className="bg-sky-600 hover:bg-sky-700">
              <Plus className="w-4 h-4 mr-2" /> Add Patient
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{fontFamily: 'Manrope'}}>Add New Patient</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="space-y-2">
                <Label>Full Name</Label>
                <Input data-testid="patient-name-input" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required placeholder="John Doe" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date of Birth</Label>
                  <Input data-testid="patient-dob-input" type="date" value={form.date_of_birth} onChange={(e) => setForm({...form, date_of_birth: e.target.value})} required />
                </div>
                <div className="space-y-2">
                  <Label>Gender</Label>
                  <Select value={form.gender} onValueChange={(v) => setForm({...form, gender: v})}>
                    <SelectTrigger data-testid="patient-gender-trigger"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Male">Male</SelectItem>
                      <SelectItem value="Female">Female</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input data-testid="patient-phone-input" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} placeholder="+1-555-0100" />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input data-testid="patient-email-input" type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} placeholder="patient@email.com" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Medical History</Label>
                <Textarea data-testid="patient-history-input" value={form.medical_history} onChange={(e) => setForm({...form, medical_history: e.target.value})} placeholder="Any known conditions, allergies..." rows={3} />
              </div>
              <Button data-testid="patient-submit-btn" type="submit" className="w-full bg-sky-600 hover:bg-sky-700">Add Patient</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          data-testid="patient-search"
          placeholder="Search patients..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold text-slate-700">Name</TableHead>
                <TableHead className="font-semibold text-slate-700">Date of Birth</TableHead>
                <TableHead className="font-semibold text-slate-700">Gender</TableHead>
                <TableHead className="font-semibold text-slate-700">Phone</TableHead>
                <TableHead className="font-semibold text-slate-700">Email</TableHead>
                <TableHead className="font-semibold text-slate-700">Medical History</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">Loading...</TableCell></TableRow>
              ) : patients.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">
                  <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No patients found</p>
                </TableCell></TableRow>
              ) : patients.map((patient) => (
                <TableRow key={patient.id} data-testid={`patient-row-${patient.id}`} className="hover:bg-slate-50">
                  <TableCell className="font-medium text-slate-900">{patient.name}</TableCell>
                  <TableCell className="text-slate-600">
                    <div className="flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5 text-slate-400" />
                      {patient.date_of_birth}
                    </div>
                  </TableCell>
                  <TableCell><Badge variant="secondary" className="font-normal">{patient.gender}</Badge></TableCell>
                  <TableCell className="text-slate-600">{patient.phone || '-'}</TableCell>
                  <TableCell className="text-slate-600">{patient.email || '-'}</TableCell>
                  <TableCell className="text-slate-600 max-w-[200px] truncate">{patient.medical_history || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
